<template>
  <div>
    <v-card class='mx-2' outlined>
      <v-card-title>
        <v-container fluid>
          {{showDeploymentOptions ? "Deployment" : "Gateway Status"}}
          <v-row>
            <v-col cols="12" sm="2" md="1" class="mt-2">
              <span><h5>Filters:</h5></span>
            </v-col>
            <v-col cols="12" sm="6" md="3" xl="2">
              <v-select
                v-model="filterConnected"
                :items="connectedStates"
                @change="saveFilters"
                label="GW Cloud Connection"
                multiple
                dense
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="3" xl="2">
              <v-select
                v-model="filterOrderTypes"
                :items="orderTypes"
                @change="saveFilters"
                label="Order Type"
                multiple
                dense
              ></v-select>
            </v-col>
            <v-spacer></v-spacer>
            <v-col cols="12" sm="6" md="3" xl="2">
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                label="Search (incl hidden cols)"
                single-line
                hide-details
                clearable
                dense
                @change="searchChange"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-container>
      </v-card-title>
      <v-data-table v-model="selected" 
        :headers="headers" 
        :items="systems" 
        :search="search" 
        show-select
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        @update:sort-by="saveSort"
        @update:sort-desc="saveSort"
        :items-per-page='parseInt("-1")'
        :footer-props="footerOptions"
        no-data-text="No Documents">
        <template v-slot:top>
          <v-toolbar flat color="white">
            <v-btn 
              color="primary" 
              class="mt-n6"
              @click="editColumns">
              Edit Cols
            </v-btn>
            <v-dialog 
              v-model="showEditColDialog" 
              max-width="50%">
              <v-card class="px-4">
                <v-card-title>
                  <span class="headline">Select Columns to Show</span>
                </v-card-title>
                <v-card-text>
                  <v-container>
                  <v-select
                    v-model="selectedColumns"
                    :items="headers"
                    multiple
                    @change="changeColumns">
                  </v-select>
                  </v-container>
                </v-card-text>
                <v-card-actions>
                  <div class="flex-grow-1"></div>
                  <v-btn color="primary" @click="editColumns">Ok</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <v-spacer></v-spacer>
            <v-combobox
              v-if=showDeploymentOptions
              v-model="playbook"
              :items="playbooks"
              placeholder="Ansible Playbook"
              class="shrink mx-2"
              outlined
              dense
              clearable
            >
            </v-combobox>
            <v-text-field
              v-if=showDeploymentOptions
              v-model="branch"
              class="shrink"
              outlined
              dense
              placeholder="Branch Name"
            >
            </v-text-field>
            <v-btn 
              v-if=showDeploymentOptions
              color="primary" 
              class="mx-2 mt-n6"
              :disabled='!enableDeploy'
              @click="deploySystems">
                Deploy
            </v-btn>

            <!-- export button -->
            <v-btn 
              v-if=!showDeploymentOptions
              color="primary" 
              class="mt-n6"
              :disabled='selected.length===0'
              @click="exportSystems">
              <v-icon left>
                fa-file-excel-o
              </v-icon>
                Export
            </v-btn>
            <v-menu
              v-else>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="primary"
                  class="mx-2 mt-n6"
                  v-bind="attrs"
                  v-on="on">
                <v-icon left>
                  fa-file-excel-o
                </v-icon>
                  Export
                </v-btn>
              </template>
              <v-list>
                <v-list-item
                  v-for="(item, index) in exportModes"
                  :key="index"
                  @click="exportSystems(item.val)">
                  <v-list-item-title>
                    {{ item.str }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>

            <!-- events button -->
            <v-menu
              v-if=!showDeploymentOptions>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="primary"
                  class="mx-2 mt-n6"
                  v-bind="attrs"
                  v-on="on">
                <v-icon left>
                  fa-refresh
                </v-icon>
                  Events
                </v-btn>
              </template>
              <v-list>
                <v-list-item
                  v-for="(item, index) in eventDurations"
                  :key="index"
                  @click="updateConnectionEvents(item.val)">
                  <v-list-item-title>
                    {{ item.str }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>

          </v-toolbar>
        </template>
        <template v-slot:item.serNum="{ item }">
          <a :href="systemsLink(item)">
            {{ item.serNum }}
          </a>
        </template>
        <template v-slot:item.hostbase="{ item }">
          <a :href="qxcontrolLink(item)">
            {{ item.hostbase }}
          </a>
        </template>
        <template v-slot:item.gwConnected="{ item }">
          <v-chip
            :color="getColor(item.gwConnected)"
            dark
            small
          >
            {{ item.gwConnected }}
          </v-chip>
        </template>
        <template v-slot:item.gwConnectionEventCount="{ item }">
          <v-tooltip left>
            <template v-slot:activator="{ on }">
              <p v-on="on">{{ item.gwConnectionEventCount }}</p>
            </template>
            <span style="white-space: pre-wrap">
              {{ formattedDateList(item.gwLastConnectionEvents) }}
            </span>
          </v-tooltip>
        </template>
        <template v-slot:item.gwOldestMedia="{ item }">
          {{ toDHHMMSS(item.gwOldestMedia) }}
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>

import { saveAs } from 'file-saver';
import env from '../../server.config'
const BAD_PASSWORD = "xxx";

export default {
  name: "DeployUi",
  props: {
    app: Object,
    showDeploymentOptions: Boolean,
    setProgressVal: Function,
    setProgressLinear: Function,
    systemSerNum: String
  },
  data: () => ({
    sortBy: "serNum",
    sortDesc: false,
    branch: '',
    search: '',
    // this list comes from: https://gitlab.qumulex.io/devops/ansible/blob/develop/deploy/Makefile
    playbooks: [
      'hostprep-host',
      'deploy-prem-clean',
      'deploy-prem-clean-stream',
      'deploy-prem-services',
      'deploy-prem-services-stream',
      'test-a-task',
      'deploy-prem-client',
      'deploy-prem-proxy',
      'deploy-prem-agent',
      'deploy-prem-minion',
      'deploy-prem-qxdc',
      'deploy-prem-qxmedia',
      'deploy-prem-hasura',
      'deploy-prem-redis',
      'deploy-prem-cronjobs',
      'deploy-prem-qxstream',
      'deploy-prem-qxsignal',
      'deploy-prem-qxrep',
      'deploy-pgnormal',
      'wipevideo-prem-machine',
      'wipestreams-prem-machine',
    ],
    playbook: '',
    systems: [],
    gateways: [],
    selected: [],
    selectedColumns: [],
    showEditColDialog: false,
    eventIdx: 0,
    eventDuration: 0,
    events: [],
    connectedStates: ['true','false', 'NotInCloud'],
    filterConnected: ['true','false', 'NotInCloud'],
    orderTypes: ["COMMERCIAL", "DEMO", "EAP", "INTERNAL", "ON_PREM_COMMMERCIAL", "ON_PREM_INTERNAL", "OUT_OF_SERVICE", "TBD", "0"],
    filterOrderTypes: ["COMMERCIAL", "DEMO", "EAP", "INTERNAL", "ON_PREM_COMMMERCIAL", "ON_PREM_INTERNAL", "OUT_OF_SERVICE", "TBD"],
    eventDurations: [
      {'str':'Past 1 hr', 'val': 1},
      {'str':'Past 3 hrs', 'val': 3},
      {'str':'Past 6 hrs', 'val': 6},
      {'str':'Past 12 hrs', 'val': 12},
      {'str':'Past 24 hrs', 'val': 24},
      {'str':'Past 3 days', 'val': 72},
      {'str':'Past 7 days', 'val': 168},
      {'str':'Past 30 days', 'val': 720},
      {'str':'Save to *.csv', 'val': -1}
    ],
    footerOptions: {
      itemsPerPageOptions: [20, 50, 100, 200, 500, -1]
    },
    exportModes: [
    {'str': "All columns", 'val': 'all'},
    {'str': "WG limit format", 'val': 'limit'},
    {'str': "Inventories format", 'val': 'inventories'}    
    ]
  }),

  created() {
    console.log('deployUi created')
  },

  mounted() {
    console.log('deployUi mounted', this.systemSerNum);
    this.playbooks.sort((a,b) => a > b ? 1 : -1);
    if (this.systemSerNum?.length) {
      this.search = this.systemSerNum;
    } else if (sessionStorage.getItem('deploySearchString')) {
      this.search = sessionStorage.getItem('deploySearchString');
    } else {
      this.search = '';
    }
    let item = sessionStorage.getItem('deployGwCloudConnectionFilter');
    if (item) this.filterConnected = item.split(",");
    item = sessionStorage.getItem('deployOrderTypesFilter');
    if (item) this.filterOrderTypes = item.split(",");
    item = sessionStorage.getItem("deployConnectionEvents"); 
    if (item) this.events = JSON.parse(item);
    item = sessionStorage.getItem("deploySortBy"); 
    if (item) this.sortBy = item;
    item = sessionStorage.getItem("deploySortDesc"); 
    if (item) this.sortDesc = item === 'true';
    this.initialize();
  },

  computed: {
    headers() {
      return [
      {
        text: 'Gateway S/N',
        align: 'left',
        value: 'serNum'
      },
      {
        text: 'Cust Place',
        align: 'left',
        value: 'gwPlace'
      },
      {
        text: 'Name',
        align: 'left',
        value: 'gwName'
      },
      {
        text: 'WG Address',
        align: 'left',
        value: 'wireguard'
      },
      {
        text: 'Hostbase',
        align: 'left',
        value: 'hostbase'
      },
      {
        text: 'Model',
        align: 'left',
        value: 'model'
      },
      {
        text: 'Date Created',
        align: 'left',
        value: 'createdDate'
      },
      {
        text: 'Order Type',
        align: 'left',
        value: 'orderType',
        filter: value => {
          if (this.filterOrderTypes.length == 0) return false;
          let found = this.filterOrderTypes.findIndex(str => {
            return str === value+"";
          })
          return found >= 0;
        },
      },
      {
        text: 'Sales Order',
        align: 'left',
        value: 'salesOrder'
      },
      {
        text: 'Integrator',
        align: 'left',
        value: 'customerId'
      },
      {
        text: 'Rep Firm',
        align: this.showDeploymentOptions ? ' d-none' : 'left',
        value: 'rep'
      },
      {
        text: 'SW Version',
        align: 'left',
        value: 'gwVersion'
      },
      {
        text: 'Dev Count',
        align: 'left',
        value: 'devCount'
      },
      {
        text: 'Oldest Media',
        align: 'left',
        value: 'gwOldestMedia'
      },
      {
        text: 'Connected',
        align: 'left',
        value: 'gwConnected',
        filter: value => {
          if (this.filterConnected.length == 0) return false;
          let found = this.filterConnected.findIndex(str => {
            return str === value+"";  // convert value: any to string
          })
          return found >= 0;
        },
      },
      {
        text: 'Connection Events',
        align: 'left',
        value: 'gwConnectionEventCount'
      },
    ]},
    enableDeploy() {
      if (this.selected.length===0 || this.branch.length===0 || this.playbook==undefined || this.playbook.length===0) {
        return false;
      }
      const deployEnv = this.selected[0].gwConnected; // NotInCloud, true, false
      for (let sel of this.selected ) {
        if ((deployEnv == "NotInCloud" && sel.gwConnected != "NotInCloud") || (deployEnv != "NotInCloud" && sel.gwConnected == "NotInCloud")) {
          return false;
        }
      }
      return true;
    }
  },

  methods: {
    initialize() {
      this.headers.forEach(header => {
        if (header.align !== " d-none") {
          this.selectedColumns.push(header.value);
        }
      }) 
      this.getSystems();
      this.getGateways();
    },

    getColor (connected) {
      if (connected == false) return 'red'
      else if (connected == true) return 'green'
      else return 'orange'
    },

    formattedDateList(timestamps) {
      let list = "";
      timestamps.sort((a, b) => {return b - a});
      for (let ii=0; ii<10 && ii<timestamps.length; ii++) {
        list += new Date(timestamps[ii]).toLocaleString() + "\n";
      }
      return list.trim();
    },

    qxcontrolLink(item) {
      if (item?.gwId)
        return env.appBaseUrl + `/qx/gateways/${item.gwId}`;
      else {
        const dotIndex = env.appBaseUrl.indexOf(".");
        const appBaseUrl = env.appBaseUrl.substring(dotIndex);
        return `https://${item.hostbase.trim()}-pub${appBaseUrl}`;
      }
    },

    systemsLink(item) {
      return window.location.protocol + "//" + window.location.host + `/OpsPortal/#/systems/${item.serNum}`; 
    },

    editColumns() {
      this.showEditColDialog = !this.showEditColDialog;
    },

    changeColumns() {
      this.headers.forEach(header => {
        header.align = " d-none";
      })
      this.selectedColumns.forEach(item => {
        let headerIdx = this.headers.findIndex(obj => {
          return obj.value === item;
        })
        if (headerIdx >= 0) {
          this.headers[headerIdx].align = "left";
        }
      })
    },

    saveFilters() {
      sessionStorage.setItem('deployGwCloudConnectionFilter', this.filterConnected);
      sessionStorage.setItem('deployOrderTypesFilter', this.filterOrderTypes);
    },

    saveSort() {
      sessionStorage.setItem("deploySortBy", this.sortBy); 
      sessionStorage.setItem("deploySortDesc", this.sortDesc); 
    },

    searchChange() {
      if (this.search) {
        sessionStorage.setItem('deploySearchString', this.search);
      } else {
        sessionStorage.setItem('deploySearchString', "");
      }
    },
    
    async exportSystems(mode = "all") {
      /* mode can be:
        1) All columns
        2) WG Limit file
        3) Inventories file
      */

      console.log("DeployUi::exportSystems()", mode)
      let name = "systems.csv";
      let exportItems = [];
      let headerString = "";
      switch (mode) {
        case 'all':
        default:
          headerString = 'Serial Number, WG Address, Hostbase, Model, Rev, Date Created, Order Type, Name, Place, SO, Integrator, Rep, Version, Dev Count, Oldest Media, Connected, Connection Events\n';
          exportItems.push(headerString);
          this.selected.forEach(item => {
            let itemString = '';
            itemString += item.serNum + ',';
            itemString += item.wireguard + ',';
            itemString += item.hostbase.trim() + ',';
            itemString += item.model + ',';
            itemString += item.rev + ',';
            itemString += item.createdDate + ',';
            itemString += item.orderType + ',';
            itemString += '"' + item.gwName + '",'; // Name may contain commas
            itemString += '"' + item.gwPlace + '",';
            itemString += '"' + item.salesOrder + '",';
            itemString += '"' + item.customerId + '",';
            itemString += '"' + item.rep + '",';
            itemString += '"' + item.gwVersion + '",';
            itemString += '"' + item.devCount + '",';
            itemString += '"' + item.gwOldestMedia + '",';
            itemString += item.gwConnected + ','; 
            itemString += item.gwConnectionEventCount + '\n'; 
            exportItems.push(itemString);
          });
          break;
        case 'limit':
          this.selected.forEach(item => {
              let itemString = item.hostbase.trim() + '-wg\n';
              exportItems.push(itemString);
          });
          break;
        case 'inventories':
          {
            this.setProgressLinear(true);
            let cntr = 0;
            for (const item of this.selected) { // use for{} instead of foreach{} to allow all the async api calls to complete
              this.setProgressVal(100*cntr++ / this.selected.length);
              const qxpwd = await this.getQadminPassword(item.serNum);
              let itemString = '';
              itemString += item.hostbase.trim() + '-wg ';
              itemString += 'ansible_host=' + item.wireguard + ' ';
              itemString += 'NAT_ADDRESS_var=' + item.wireguard + ' ';
              if (!qxpwd || qxpwd == "Unknown" || qxpwd == BAD_PASSWORD) {
                itemString += `ansible_ssh_private_key_file=~/.ssh/${item.hostbase.trim()}.pem\n`; // ansible_ssh_private_key_file=~/.ssh/qxed0033.pem
              } else {
                itemString += 'ansible_user=qadmin ';
                itemString += 'ansible_ssh_pass=' + '"' + qxpwd + '"\n';
              }
              exportItems.push(itemString);
            }
            this.setProgressLinear(false);
          }
          break;
      }
      let blob = new Blob(exportItems, {
        type: "text/csv"
      });
      saveAs(blob, name);
    },

    deploySystems() {
      var msg = "Are you sure you want to deploy playbook: " + this.playbook + " and branch: "
      msg += this.branch + " to the " + this.selected.length + " selected gateways?";
      if (confirm(msg)) {
        this.postGatewaysDeploy();
      }
    },

    prepareSystems() {
      const {visibleCompanies} = this.app.userProfile;
      for (let system of this.systems) {
        const octets = system.wireguard.split('.')
        let hostbase = " qx" + parseInt(octets[1]).toString(16).padStart(2,'0');
        hostbase += parseInt(octets[2]).toString(16).padStart(2,'0');
        hostbase += parseInt(octets[3]).toString(16).padStart(2,'0');
        system.hostbase = hostbase;
        system.gwName = "tbd";
        system.gwPlace = "tbd";
        system.gwPlaceId = "";
        system.gwConnected = "NotInCloud";
        system.gwConnectionEventCount = "n/a";
        system.gwLastConnectionEvents = [];
        system.gwVersion = "tbd";
        system.gwOldestMedia = "tbd";
        system.devCount = "-";
        system.rep = "";
        // Match system customer id to visible companies
        let idx = visibleCompanies.findIndex(obj => {
          return !!system.customerId && system.customerId.length > 0 && obj.customerId === system.customerId;
        })
        if (idx >= 0) {
          system.customerId = visibleCompanies[idx].name;
          system.rep = visibleCompanies[idx].rep
        }
      }
    },

    matchGateways() {
      console.log('matchGateways()')
      var gwIndex;
      const now = Date.now();
      this.systems.forEach(system => {
        gwIndex = this.gateways.findIndex(obj => {
          return obj.wireguardIP === system.wireguard
        })
        if (gwIndex >= 0) {
          //console.log('matchGateways() found:' + system.wireguard)
          system.gwId = this.gateways[gwIndex]?.id;
          system.gwName = this.gateways[gwIndex]?.name;
          system.gwPlace = this.gateways[gwIndex]?.place?.toHomePlaces?.customerPlace?.name;
          system.gwPlaceId = this.gateways[gwIndex]?.place?.id;
          system.gwConnected = this.gateways[gwIndex]?.connectionStatus;
          system.gwVersion = this.gateways[gwIndex]?.version;
          const gwOldestTimestamp = Date.parse(this.gateways[gwIndex]?.oldestMedia);
          system.gwOldestMedia = now - gwOldestTimestamp;
          system.devCount = this.gateways[gwIndex]?.devices_aggregate.aggregate.count;
          if (this.events.length) {
            system.gwConnectionEventCount = 0;
          } else {
            system.gwConnectionEventCount = "tbd";
          }
        }
      })
      this.$set(this.systems, 0, this.systems[0]); // reactive
    },

    toDHHMMSS(ms) {
      if (!Number.isInteger(ms)) return "N/A"
      const sec = ms / 1000;
      const years = Math.floor(sec / (365*86400));
      const days =  Math.floor((sec - (years * 365*86400)) / 86400);
      let hours   = Math.floor((sec - (years * 365*86400) - (days * 86400)) / 3600);
      let minutes = Math.floor((sec - (years * 365*86400) - (days * 86400) - (hours * 3600)) / 60);
      let seconds = Math.floor(sec - (years * 365*86400) - (days * 86400) - (hours * 3600) - (minutes * 60));

      if (hours   < 10) {hours   = "0"+hours;}
      if (minutes < 10) {minutes = "0"+minutes;}
      if (seconds < 10) {seconds = "0"+seconds;}
      return years + 'Y, ' + days + 'D, ' + hours+':'+minutes+':'+seconds;
    },

    async updateConnectionEvents(duration) {
      console.log("DeployUi::updateConnectionEvents()")
      if (duration < 0) {
        this.exportConnectionEvents();
        return;
      }
      this.events = [];
      this.eventIdx = 0;
      this.eventDuration = duration;
      this.systems.forEach(system => {
        system.gwConnectionEventCount = system.gwConnected === 'NotInCloud' ? 'n/a' : 0;
        system.gwLastConnectionEvents = [];
      });
      const limit = 10;  // arbitrary
      let count = 0;
      let end = new Date();
      let start = new Date(end.getTime() - duration*60*60*1000);
      let startIso = start.toISOString();
      let endIso = end.toISOString();
      do {
        count = await this.getConnectionEvents(limit, startIso, endIso);
        const eventCount = this.events.length;
        console.log("events return", eventCount)
        endIso = eventCount > 0 ? this.events[eventCount-1].serverTime : "";
      } while(count && count >= limit);
      sessionStorage.setItem("deployConnectionEvents", JSON.stringify(this.events));
    },

    async exportConnectionEvents() {
      let name = "events.csv";
      let exportItems = [];
      const headerString = 'id, type, active, serverTime, serial, orderType\n';
      exportItems.push(headerString);
      this.events.forEach((event) => {
        let eventString = event?.id + ',';
        eventString += event?.type + ',';
        eventString += event?.active + ',';
        eventString += event?.serverTime + ',';
        eventString += event?.place?.gateway?.serial + ',';
        eventString += this.getOrderType(event?.place?.gateway?.serial) + '\n';
        exportItems.push(eventString);
      });
      let blob = new Blob(exportItems, {
        type: "text/csv"
      });
      saveAs(blob, name);
    },

    getOrderType(serNum) {
      const found = this?.systems?.find((system) => system?.serNum == serNum);
      if (found) return found?.orderType ?? "";
      return "";
    },
    
    matchConnectionEvents(events) {
      if (events?.length > 0) this.eventIdx = events[0].id;
      events.forEach(event => {
        let gwIndex = this.systems.findIndex(obj => {
          return obj.gwPlaceId === event.placeID
        })
        if (gwIndex >= 0) {
          this.systems[gwIndex].gwConnectionEventCount++;
          this.systems[gwIndex].gwLastConnectionEvents.push(Date.parse(event.serverTime));
          this.$set(this.systems, gwIndex, this.systems[gwIndex]); // use $set to make array reactive
        }
      });
    },

    getSystems() {
      console.log('/API/public/systems')
      this.app.restApi({
          method: 'get',
          url: '/API/public/systems',
          headers: {
            'Authorization': 'Bearer ' + this.app.userProfile.token
          }
        })
        .then(response => {
          this.systems = Object.values(response.data);
          this.prepareSystems();
          this.getGateways();
        })
        .catch( error => {
          console.log(error);
          alert("Failed to get systems from sysprep database. error=" + error.response.status)
        });
    },

    getGateways() {
      console.log('/API/public/systems/gateways')
      this.app.restApi({
          method: 'get',
          url: '/API/public/systems/gateways',
          headers: {
            'Authorization': 'Bearer ' + this.app.userProfile.token
          }
        })
        .then(response => {
          console.log("Success: /API/public/systems/gateways");
          this.gateways = Object.values(response.data);
          this.matchGateways();
          this.matchConnectionEvents(this.events);  // populate events with cached event list
        })
        .catch( error => {
          console.log(error);
          alert("Failed to get gateways from QxControl cloud. error=" + error?.response?.status)
        });
    },

    postGatewaysDeploy() {
      console.log('/API/public/systems/gateways/deploy')
      const wgUuids = [];
      let deployEnv = this.selected[0].gwConnected == "NotInCloud" ? "premise" : "gateway";
      this.selected.forEach(item => {
        wgUuids.push(item.wgUuid);
        if (item.gwConnected == "NotInCloud" ? "premise" : "gateway" != deployEnv) {
          deployEnv = "";   // check that systems are either all NotInCloud or all Cloud
        }
      });
      if (deployEnv == "") return;  
      this.app.restApi({
          method: 'post',
          url: '/API/public/systems/gateways/deploy',
          headers: {
            'Authorization': 'Bearer ' + this.app.userProfile.token
          },
          data: {
            "prodLevel": env.prodLevel,
            "playbook": this.playbook,
            "branch": this.branch,
            "gwenv": deployEnv,
            "uuids": [...wgUuids]
          }
        })
        .then(response => {
          console.log("Success: /API/public/systems/gateways/deploy");
          this.gateways = Object.values(response.data);
          this.matchGateways();
        })
        .catch( error => {
          console.log(error);
          alert("Failed to post gateways for deploy. error=" + error.response.status)
        });
    },

    async getConnectionEvents(limit, startTime, endTime) {
      console.log('DeployUi::getConnectionEvents() /API/public/systems/events')
      let url = '/API/public/systems/events'
      url += "?evtType=GatewayConnection"
      url += "&startTime=" + encodeURIComponent(startTime)
      url += "&endTime=" + encodeURIComponent(endTime)
      url += "&limit=" + limit
      url += "&aggregate=false"
      let count = 0;
      await this.app.restApi({
          method: 'get',
          url: url,
          headers: {
            'Authorization': 'Bearer ' + this.app.userProfile.token
          }
        })
        .then(response => {
          console.log("DeployUi::getConnectionEvents() Success: /API/public/systems/events");
          if (response?.data) {
            this.events = this.events.concat(response.data);
            this.matchConnectionEvents(Object.assign(response.data));
            count = response.data.length;
          } else {
            count = 0;
          }
        })
        .catch( error => {
          console.log("DeployUi::getConnectionEvents() error: " + error);
          // This doesn't work if I kick off multiple requests at once - need to parse by id
          let errString;
          if (error?.response?.status == 408) {
            errString = "Timeout";
          } else {
            errString =  "error: " + error?.response?.status;
          }
          alert("Error in obtaining connection events from backend: " + errString);
          count = 0;
        });
      return count;
    },

    async getQadminPassword(id) {
      let url = '/API/public/system/' + id + '/secrets'
      let qxpwd = BAD_PASSWORD;
      url += "?prodLevel=" + env.prodLevel
      await this.app.restApi({
          method: 'get',
          url: url,
          headers: {
            'Authorization': 'Bearer ' + this.app.userProfile.token
          }
        })
        .then(response => {
           for (let i = 0; i < response.data.split("\n").length; i++) {
             if (response.data.split("\n")[i] == "qadmin_pwd:") {
                qxpwd = response.data.split("\n")[i+1];
             }
           }
        })
        .catch( error => {
          alert("Error getting password for " + id + ": " + error?.response?.status);
        });
      if (qxpwd == BAD_PASSWORD) {
        alert("No qadmin password for " + id);
      }
      return qxpwd;
    },
  }
}
</script>